import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ChevronRight, ExternalLink, MapPin, Check, Share2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

const HERO_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/JrhUEHsfckCWpGfz.webp";
const ARTICLE_IMG_2 = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/nNPjTUZQmROOgijy.webp";

const ARTICLE2_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp";
const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE5_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-piata-sfatului-night_37c559b4.webp";

const RELATED = [
  { tag: "City Guide", title: "What to Do in Brașov: The Complete City Guide for Expats", img: ARTICLE2_IMG, href: "/what-to-do-in-brasov-city-guide" },
  { tag: "City Guide", title: "Why Move to Brașov? A Foreigner's Honest Guide to Romania's Best City", img: ARTICLE1_IMG, href: "/why-expats-choose-brasov" },
  { tag: "City Guide", title: "Entertainment in Brașov: Day & Night Guide for Adults", img: ARTICLE5_IMG, href: "/entertainment-brasov-guide" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60"
        style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
      >
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
          alt="Cartier Transilvania Brașov"
          className="w-full h-48 object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
        >
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
          alt="Cartier Transilvania"
          className="w-full md:w-56 h-40 object-cover flex-shrink-0"
          loading="lazy"
        />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
          >
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleBlackChurch() {
  return (
    <>
      <Helmet>
        <title>The Black Church Brașov: Tickets, Hours & Visitor Guide (2026)</title>
        <meta name="description" content="Everything you need to visit the Black Church in Brașov — ticket prices, opening hours, best time to go, organ concerts, and what to see inside. Updated for 2026." />
        <meta property="og:title" content="The Black Church Brașov: Tickets, Hours & Visitor Guide (2026)" />
        <meta property="og:description" content="Everything you need to visit the Black Church in Brașov — ticket prices, opening hours, best time to go, organ concerts, and what to see inside. Updated for 2026." />
        <meta property="og:image" content="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/iuQHHSuPYIVWdtOM.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="The Black Church in Brașov's Council Square" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://brasovgateway.com/black-church-brasov-visitor-guide" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Black Church Brașov: Tickets, Hours & Visitor Guide (2026)" />
        <meta name="twitter:description" content="Everything you need to visit the Black Church in Brașov — ticket prices, opening hours, best time to go, organ concerts, and what to see inside. Updated for 2026." />
        <meta name="twitter:image" content="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/iuQHHSuPYIVWdtOM.webp" />
        <meta name="keywords" content="Black Church Brasov, Biserica Neagra Brasov, Black Church tickets, Black Church opening hours, Brasov landmarks, visit Black Church" />
        <link rel="canonical" href="https://brasovgateway.com/black-church-brasov-visitor-guide" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "@id": "https://brasovgateway.com/black-church-brasov-visitor-guide#article",
                "headline": "The Black Church in Brașov: what it actually looks like inside, and how to plan the visit",
                "description": "Everything you need to visit the Black Church in Brașov — ticket prices, opening hours, best time to go, organ concerts, and what to see inside. Updated for 2026.",
                "image": "${HERO_IMG}",
                "datePublished": "2026-04-22",
                "dateModified": "2026-04-22",
                "author": {
                  "@type": "Organization",
                  "name": "Brașov Gateway",
                  "url": "https://brasovgateway.com"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Brașov Gateway",
                  "url": "https://brasovgateway.com"
                },
                "url": "https://brasovgateway.com/black-church-brasov-visitor-guide",
                "inLanguage": "en",
                "articleSection": "City Guide"
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://brasovgateway.com/black-church-brasov-visitor-guide#breadcrumb",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://brasovgateway.com" },
                  { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://brasovgateway.com/blog" },
                  { "@type": "ListItem", "position": 3, "name": "The Black Church Brașov: Visitor Guide 2026", "item": "https://brasovgateway.com/black-church-brasov-visitor-guide" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
        <SiteHeader />

        {/* Hero */}
        <div className="relative h-72 md:h-96 mt-16">
          <img
            src={HERO_IMG}
            alt="The Black Church in Brașov's Council Square"
            className="w-full h-full object-cover"
            fetchPriority="high"
            width="1600"
            height="900"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
        </div>

        <div className="container py-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main content */}
            <main className="flex-1 min-w-0">
              <article className="max-w-none">
                <header className="mb-10">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-xs font-bold uppercase tracking-widest px-3 py-1" style={{ backgroundColor: "oklch(0.38 0.12 15)", color: "white" }}>
                      City Guide
                    </span>
                    <span className="text-xs text-stone-500 uppercase tracking-widest">April 2026</span>
                  </div>
                  <h1 className="text-stone-900 leading-tight mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800 }}>
                    The Black Church Brașov: what it actually looks like inside, and how to plan the visit
                  </h1>
                  <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        <img
                          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
                          alt="Brașov Gateway"
                          className="w-full h-full object-cover opacity-50"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                        <p className="text-xs text-stone-500">City Guide Team</p>
                      </div>
                    </div>
                    <ShareButton url="https://brasovgateway.com/black-church-brasov-visitor-guide" title="The Black Church Brașov: Tickets, Hours & Visitor Guide (2026)" />
                  </div>
                </header>

                {/* Lead paragraph */}
                <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed mt-6 mb-8" style={{ lineHeight: "1.6" }}>
                  You will walk into Council Square, look up, and the Black Church Brașov will be right there — dark stone, Gothic towers, rising above the café tables like it has been doing for six centuries. The first view is free. Everything after that takes a bit of planning.
                </p>

                {/* Quick Facts */}
                <div className="my-10 p-6 border-l-4" style={{ backgroundColor: "oklch(0.97 0.005 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
                  <h3 className="text-xs uppercase tracking-widest font-bold mb-5" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Quick facts: Black Church Brașov
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: "Address", value: "Curtea Johannes Honterus 2", note: "Council Square, Old Town Brașov" },
                      { label: "Adult ticket", value: "30 RON (~€6)", note: "Cash or card accepted at the entrance" },
                      { label: "Student / Senior ticket", value: "25 RON (~€5)", note: "Valid student card or pension coupon required" },
                      { label: "Opening hours (summer)", value: "Tue–Sat 10:00–18:00", note: "Mon closed; last entry 20 min before closing" },
                      { label: "Opening hours (winter)", value: "Tue–Sat 10:00–15:00", note: "Hours shift seasonally — always verify on the official site" },
                      { label: "Organ concerts", value: "June–August, Tuesdays", note: "Separate ticket required; check schedule at bisericaneagra.ro" },
                    ].map((fact, i) => (
                      <div key={i} className="p-4 border border-stone-100" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                        <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">{fact.label}</p>
                        <p className="font-bold text-stone-900 text-sm leading-tight mb-1" style={{ fontFamily: "Playfair Display, serif" }}>{fact.value}</p>
                        <p className="text-xs text-stone-500 font-light">{fact.note}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Table of Contents */}
                <div className="my-8 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>In this guide</p>
                  <ol className="space-y-2">
                    {[
                      { n: "01", label: "Tickets, entry rules, and what to bring", anchor: "#tickets" },
                      { n: "02", label: "Opening hours and the best time to visit", anchor: "#hours" },
                      { n: "03", label: "What you will actually see inside", anchor: "#inside" },
                      { n: "04", label: "The Anatolian carpets: the detail nobody expects", anchor: "#carpets" },
                      { n: "05", label: "Organ concerts: when and how to attend", anchor: "#concerts" },
                      { n: "06", label: "Three things most visitors walk past", anchor: "#hidden" },
                      { n: "07", label: "Building a half-day around the church", anchor: "#half-day" },
                    ].map((item) => (
                      <li key={item.n} className="flex items-start gap-3">
                        <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }}>{item.n}</span>
                        <a href={item.anchor} className="text-stone-600 text-sm font-light hover:text-stone-900 transition-colors no-underline" style={{ lineHeight: "1.5" }}>
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Section 1 — Tickets */}
                <h2 id="tickets" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Tickets, entry rules, and what to bring
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Entry to the Black Church is paid. Tickets are sold at the church entrance — no advance booking required, no third-party platform needed. The current prices, updated for 2026, are as follows:
                </p>

                {/* Ticket Price Table */}
                <div className="my-6 w-full overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
                  <table className="border-collapse" style={{ width: "100%", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}>
                    <thead>
                      <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        {["Category", "Price (RON)", "Price (approx. EUR)", "Notes"].map((h) => (
                          <th key={h} className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "0.6rem 0.8rem", fontSize: "0.7rem", letterSpacing: "0.05em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { cat: "Adults", ron: "30 RON", eur: "~€6", note: "No ID required" },
                        { cat: "Students", ron: "25 RON", eur: "~€5", note: "Valid student card required" },
                        { cat: "Seniors (65+)", ron: "25 RON", eur: "~€5", note: "Pension coupon required" },
                        { cat: "Children (under 7)", ron: "Free", eur: "Free", note: "No ticket needed" },
                        { cat: "Guided tour", ron: "Included", eur: "Included", note: "Free with entry, subject to availability" },
                      ].map((row, i) => (
                        <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "oklch(0.985 0.006 75)" : "oklch(0.975 0.005 75)" }}>
                          <td className="text-stone-700 font-medium border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>{row.cat}</td>
                          <td className="text-stone-700 font-semibold border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>{row.ron}</td>
                          <td className="text-stone-500 border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>{row.eur}</td>
                          <td className="text-stone-500 font-light border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>{row.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Both cash and card are accepted. The church is still an active place of worship, not a museum — so dress reasonably (no beachwear, no sleeveless tops), keep your voice low once you are inside, and check whether photography is permitted on the day you visit. Rules can change during services or special events. The guided tour, when available, is included in the ticket price and worth taking if your timing lines up — it adds context that the posted signs alone do not fully provide.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  One practical note: ticket prices and opening hours on third-party sites are often outdated. Always verify on the{" "}
                  <a
                    href="https://bisericaneagra.ro/en/program/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors"
                    style={{ color: "oklch(0.38 0.12 15)" }}
                  >
                    official site
                  </a>{" "}
                  before you go. The church occasionally closes for services, rehearsals, or special events, and knowing that in advance saves a wasted trip across the Old Town.
                </p>

                {/* Section 2 — Hours */}
                <h2 id="hours" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Opening hours and the best time to visit
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The Black Church is closed on Mondays. Tuesday through Saturday, it opens at 10:00. Closing time shifts by season: 18:00 in summer, 15:00 in winter. The last entry is accepted 20 minutes before closing — arrive earlier than you think you need to.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  For timing within the day, early morning is the most reliable choice. Groups tend to arrive mid-morning and thin out again by early afternoon. High summer — July and August — is when Council Square gets genuinely crowded, and the church interior can feel rushed if you arrive at the wrong hour. Spring (April–May) and early autumn (September–October) offer the best combination of mild weather and manageable visitor numbers.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Winter visits are underrated. If the Christmas market is running in Council Square, the church pairs well with it — the contrast between the festive square and the dark interior of a Gothic church built in the 1380s is not something you get at many other places in Europe. Just allow extra time: cold weather slows everything down, and the shorter winter hours mean you have less margin.
                </p>

                {/* Section 3 — Inside */}
                <h2 id="inside" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  What you will actually see inside
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The Black Church is the largest Gothic church in Romania and one of the most significant in southeastern Europe. Construction began in the late 14th century on the site of an earlier church destroyed by the Mongol invasion. The building you walk into today is the result of roughly a century of construction, a major fire in 1689 that blackened the walls and gave the church its name, and several centuries of repair and adaptation since.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The scale hits you first. The nave is tall and long, and the light — filtered through stone and narrow windows — lands on the floor and pews in a way that makes the space feel larger than the exterior suggests. Then the details start to register: the carved wooden pews, the stone arches, the inscriptions on the walls, and the organ at the far end, which is one of the largest in Romania.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Plan for 45 to 90 minutes. Less than that and you will rush past things worth pausing at. More than that is possible if you attend a guided tour or a concert, but for a standard visit, 60 minutes is a reasonable target.
                </p>

                {/* In-article image */}
                <div className="my-10">
                  <img
                    src={ARTICLE_IMG_2}
                    alt="Interior of the Black Church Brașov — Gothic nave, organ and Anatolian carpets"
                    className="w-full object-cover"
                    style={{ maxHeight: "480px" }}
                    loading="lazy"
                  />
                  <p className="text-xs text-stone-400 mt-2 font-light">The interior of the Black Church — Gothic nave, carved pews, and the famous Anatolian carpet collection.</p>
                </div>

                {/* Section 4 — Carpets */}
                <h2 id="carpets" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  The Anatolian carpets: the detail nobody expects
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The thing that surprises most visitors is the carpet collection. The Black Church holds one of the largest collections of Anatolian carpets in Europe outside of Turkey — over 100 pieces, most of them donated by Brașov's Saxon merchant community between the 16th and 18th centuries. They hang from the balconies and walls, adding colour and warmth against the dark stone in a way that feels completely unexpected in a Gothic church in Transylvania.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The carpets are not decoration for decoration's sake. They are a record of trade. Brașov sat at a crossroads between the Ottoman Empire and Central Europe, and the Saxon merchants who built their wealth here brought these carpets back as status symbols and gifts to the church. Walking past them, you are looking at the physical evidence of a commercial network that stretched from Anatolia to the Baltic — routed through this square, through this city, for several hundred years.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  If you want to understand Brașov's history beyond the usual "medieval Saxon city" summary, the carpet collection is where it becomes concrete. The{" "}
                  <Link href="/why-expats-choose-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    honest guide for expats
                  </Link>{" "}
                  covers more of this history in the context of why the city still has the character it does today.
                </p>

                {/* Section 5 — Concerts */}
                <h2 id="concerts" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Organ concerts: when and how to attend
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The organ at the Black Church is one of the largest pipe organs in Romania, built by Carl Hesse in 1839 and restored several times since. It has 4,000 pipes. When it plays in a space this size, the sound is not background music — it fills the building in a way that changes how the architecture feels.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Organ concerts are held regularly during summer, typically on Tuesday evenings in June, July, and August. Tickets are sold separately from the standard entry ticket — check the current schedule and prices at{" "}
                  <a
                    href="https://bisericaneagra.ro/en/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors"
                    style={{ color: "oklch(0.38 0.12 15)" }}
                  >
                    bisericaneagra.ro
                  </a>
                  . Concerts can affect access to the church for standard visits on the same day, so if you are planning both, check the schedule first and plan your visit accordingly.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  If you are in Brașov during summer and the timing works, the concert is worth attending. It is one of those experiences that is genuinely difficult to replicate elsewhere — the combination of the instrument, the space, and the acoustic is specific to this building.
                </p>

                {/* Section 6 — Hidden details */}
                <h2 id="hidden" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Three things most visitors walk past
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The church rewards slow visitors. Most people move through quickly, photograph the nave, and leave. These three details are worth stopping for.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  <strong className="text-stone-800">The fire damage that was never fully erased.</strong> The 1689 fire — set by Austrian troops during a military campaign — blackened the walls and gave the church its name. What you see now is not a pristine restoration. The building carries the damage alongside the repairs, and that layering gives it a honesty that fully restored monuments often lack. Look at the stone near the base of the walls.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  <strong className="text-stone-800">The inscriptions on the pews.</strong> The old wooden pews are carved with names and dates — records of the families who owned them, going back centuries. They are easy to walk past as generic "old wood," but each inscription is a specific person who sat in this building, in this city, at a specific point in its history.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  <strong className="text-stone-800">The organ from below.</strong> Most visitors photograph the organ from a distance. Standing directly beneath it and looking up gives a different sense of its scale. The instrument is not decorative — it was built to be heard in this specific room, and the engineering decisions that went into placing it where it is become more visible from that angle.
                </p>

                {/* Section 7 — Half-day plan */}
                <h2 id="half-day" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Building a half-day around the church
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The Black Church is in the middle of the Old Town, which means everything worth doing nearby is walkable. A half-day that starts at the church and uses the surrounding area well looks roughly like this: arrive at Council Square early, take in the exterior before the groups arrive, visit the church for 60–90 minutes, walk to Strada Sforii (one of the narrowest streets in Europe, two minutes away), find a café on or near the square for lunch, and if the weather is clear, add the Tâmpa Cable Car in the afternoon for a view over the city.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  That itinerary runs three to five hours depending on pace. It does not require a car, a guide, or advance booking for anything except the cable car in peak season. The{" "}
                  <Link href="/what-to-do-in-brasov-city-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    complete city guide for Brașov
                  </Link>{" "}
                  covers the rest of the Old Town and day trips from the city if you have more time.
                </p>

                <blockquote className="border-l-4 pl-6 my-10 italic text-stone-600 font-light" style={{ borderLeftColor: "oklch(0.38 0.12 15)", lineHeight: "1.8", fontSize: "1.1rem" }}>
                  The Black Church is not a box to tick. It is the kind of place that sharpens everything around it — the square, the streets, the city. Plan the visit properly and you will leave with more than photographs.
                </blockquote>

                {/* CTA */}
                <div className="my-12 p-8 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: "oklch(0.38 0.12 15)" }}>Thinking about more than a visit?</p>
                  <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
                    Brașov is a city worth living in, not just visiting
                  </h3>
                  <p className="text-stone-600 font-light leading-relaxed mb-5" style={{ lineHeight: "1.7" }}>
                    If you are evaluating Brașov as a place to relocate or invest, we can help. Real estate advisory, company setup, and relocation support — all in one place. Have a look at{" "}
                    <Link href="/cost-of-living-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                      the 2026 cost of living numbers
                    </Link>{" "}
                    or get in touch directly.
                  </p>
                  <Link href="/contact">
                    <button
                      className="text-xs text-white px-6 py-3 transition-opacity hover:opacity-90"
                      style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
                    >
                      GET IN TOUCH
                    </button>
                  </Link>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-8 mb-4">
                  {["Brașov", "City Guide", "Landmarks", "Tourism", "Old Town", "Expat life"].map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 border border-stone-200 text-stone-500 font-light">
                      {tag}
                    </span>
                  ))}
                </div>

                <FeaturedDeveloper />
              </article>
            </main>

            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="sticky top-24 flex flex-col gap-6">
                <SidebarAd />
                <div className="p-5 border border-stone-200 bg-white">
                  <h4 className="font-bold text-stone-900 mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    About Brașov Gateway
                  </h4>
                  <p className="text-stone-500 text-sm font-light leading-relaxed mb-4">
                    We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support — all in one place.
                  </p>
                  <a
                    href="/#contact"
                    className="block text-center text-xs text-white py-2.5 transition-opacity hover:opacity-90 no-underline"
                    style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
                  >
                    GET IN TOUCH
                  </a>
                </div>
                <div className="mt-6 p-5" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
                  <h4 className="font-bold text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                    Stay in the loop
                  </h4>
                  <p className="text-stone-300 text-xs font-light leading-relaxed mb-4">
                    No spam. One email per week with what is actually happening in Brașov.
                  </p>
                  <NewsletterInlineLight />
                </div>
                <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                  <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Real estate services</p>
                  <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                    Looking for an apartment in Brașov?
                  </h4>
                  <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                    We help foreigners find, evaluate, and buy property in Brașov — without the guesswork.
                  </p>
                  <Link href="/services/real-estate">
                    <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                      VIEW SERVICE
                    </button>
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {/* Related articles */}
          <div className="mt-20 pt-12 border-t border-stone-200">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
              <span className="text-xs uppercase tracking-widest text-stone-400">More from the blog</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {RELATED.map((post, i) => (
                <Link key={i} href={post.href} className="group cursor-pointer no-underline block">
                  <div className="overflow-hidden mb-4">
                    <img src={post.img} alt={post.title} className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>
                    {post.tag}
                  </span>
                  <h3 className="mt-2 text-base font-semibold text-stone-900 group-hover:text-stone-600 transition-colors leading-snug" style={{ fontFamily: "Playfair Display, serif" }}>
                    {post.title}
                  </h3>
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                    READ MORE <ChevronRight size={11} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <SiteFooter />
      </div>
    </>
  );
}
